
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { SupabaseAnalytics, RecipeTime } from "@/types/analytics";

export function useAnalyticsData() {
  return useQuery({
    queryKey: ["analytics"],
    queryFn: async () => {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) {
        console.error('Auth error:', authError);
        throw authError;
      }
      if (!user) {
        console.error('No authenticated user found');
        throw new Error("Not authenticated");
      }

      console.log("Fetching analytics for user:", user.id);

      // Fetch analytics data
      const { data: analyticsData, error: analyticsError } = await supabase
        .from("analytics")
        .select("*")
        .eq('user_id', user.id)
        .maybeSingle();

      if (analyticsError) {
        console.error('Error fetching analytics:', analyticsError);
        throw analyticsError;
      }

      if (!analyticsData) {
        console.error('No analytics data found for user');
        throw new Error("Analytics data not found");
      }

      console.log('Raw analytics data:', analyticsData);

      // Ensure recipes_by_category is properly typed
      const recipes_by_category: Record<string, number> = 
        typeof analyticsData.recipes_by_category === 'object' && analyticsData.recipes_by_category !== null
          ? analyticsData.recipes_by_category as Record<string, number>
          : {};

      // Type check and cast the analytics data
      const typedAnalyticsData: SupabaseAnalytics = {
        ...analyticsData,
        recipes_by_category,
        fastest_recipe: analyticsData.fastest_recipe ? (analyticsData.fastest_recipe as unknown as RecipeTime) : null,
        longest_recipe: analyticsData.longest_recipe ? (analyticsData.longest_recipe as unknown as RecipeTime) : null,
        // Ensure other nullable fields have proper defaults
        total_likes: analyticsData.total_likes || 0,
        recipe_count: analyticsData.recipe_count || 0,
        favorite_recipes: analyticsData.favorite_recipes || 0,
        total_cooking_time: analyticsData.total_cooking_time || 0,
        most_used_ingredient: analyticsData.most_used_ingredient || 'None',
        recipes_created_last_month: analyticsData.recipes_created_last_month || 0,
        recipes_created_last_week: analyticsData.recipes_created_last_week || 0,
        total_calories: analyticsData.total_calories || 0,
        most_popular_recipe: analyticsData.most_popular_recipe || 'None',
        earnings: analyticsData.earnings || 0
      };

      return typedAnalyticsData;
    }
  });
}
